import React, { FC, useEffect, useMemo, useRef, useState } from 'react';
import { Dropdown, Spin } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import { useNavigate, useSearchParams } from 'react-router-dom';
import InfiniteScroll from 'react-infinite-scroll-component';

import {
  ChatConversationItem,
  useGetTrainerMessagesPage,
} from 'src/api/mutations/useGetTrainerMessages';
import { useGetTrainerClients } from 'src/api/mutations/useGetTrainerClients';
import { useChatRealtime } from 'src/hooks/useChatRealtime';
import { useInquiriesRealtime } from 'src/hooks/useInquiriesRealtime';
import { useCheckinsRealtime } from 'src/hooks/useCheckinsRealtime';
import { auth } from 'src/config/firebase';
import { InquiriesList } from '../InquiriesList';
import { CheckinsList } from '../../../Checkins/CheckinsList';
import { PushDevicesManager } from 'src/components/PushDevicesManager/PushDevicesManager';
import { getUserPushSubscriptions, getCurrentFCMToken } from 'src/services/push/registerPush';
import { sessionStore } from 'src/store/sessionStore';

import { ReactComponent as SearchIcon } from 'src/assets/images/onboarding-trainer/dashboard/clients/search.svg';
import { ReactComponent as FilterIcon } from 'src/assets/images/onboarding-trainer/dashboard/clients/filter.svg';
import { ReactComponent as EmptyMessagesIcon } from 'src/assets/images/onboarding-trainer/dashboard/messages/empty-messages.svg';
import { ReactComponent as ClearSearchIcon } from 'src/assets/images/onboarding-trainer/dashboard/messages/clear-search.svg';
import { ReactComponent as SupportHeadsetIcon } from 'src/assets/images/onboarding-trainer/dashboard/messages/chat-support-headset.svg';
import './MessagesSidePanel.scss';

const SORT_ITEMS = [
  { key: 'newest', label: 'Date (Newest)' },
  { key: 'oldest', label: 'Date (Oldest)' },
  { key: 'name_asc', label: 'Name (A-Z)' },
];

export interface MessagesSidePanelProps {
  activeChatId?: string;
  activeCheckinId?: string;
  activeInquiryId?: string;
  onConversationsLoaded?: (conversations: ChatConversationItem[]) => void;
}

export const MessagesSidePanel: FC<MessagesSidePanelProps> = ({
  activeChatId,
  activeCheckinId,
  activeInquiryId,
  onConversationsLoaded,
}) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const currentUser = sessionStore((state) => state.currentUser);
  const trainerUid = auth.currentUser?.uid || currentUser?.uid || '';
  const [pushManagerOpen, setPushManagerOpen] = useState(false);
  const [isPushEnabled, setIsPushEnabled] = useState<boolean>(false);
  const [isPushLoading, setIsPushLoading] = useState<boolean>(true);
  const isInitialPushCheckRef = useRef<boolean>(true);

  // Check push subscription state
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      if (!user) {
        setIsPushLoading(false);
        isInitialPushCheckRef.current = false;
        setIsPushEnabled(false);
        return;
      }
      if (isInitialPushCheckRef.current) {
        setIsPushLoading(true);
      }
      try {
        const [subs, token] = await Promise.all([
          getUserPushSubscriptions(user.uid, 'trainer'),
          getCurrentFCMToken(),
        ]);
        const hasPermission =
          typeof window !== 'undefined' && 'Notification' in window
            ? Notification.permission === 'granted'
            : false;
        const storedSubId =
          typeof window !== 'undefined' ? localStorage.getItem('minipt_current_fcm_sub_id') : null;
        const isThisDeviceRegistered = subs.some(
          (s) => (token && s.fcm_token === token) || (storedSubId && s.id === storedSubId),
        );
        setIsPushEnabled(isThisDeviceRegistered && hasPermission);
      } catch {
        setIsPushEnabled(false);
      } finally {
        if (isInitialPushCheckRef.current) {
          setIsPushLoading(false);
          isInitialPushCheckRef.current = false;
        }
      }
    });

    return () => unsubscribe();
  }, [pushManagerOpen]);

  // Tab state: default from query param, or inferred from route context
  const tabFromUrl = searchParams.get('tab') as 'messages' | 'inquiries' | 'checkins' | null;
  const inferredTab: 'messages' | 'inquiries' | 'checkins' = activeCheckinId
    ? 'checkins'
    : activeInquiryId || activeChatId?.startsWith('support_')
      ? 'inquiries'
      : tabFromUrl || 'messages';

  const [activeTab, setActiveTab] = useState<'messages' | 'inquiries' | 'checkins'>(inferredTab);

  useEffect(() => {
    if (tabFromUrl) {
      setActiveTab(tabFromUrl);
    } else if (activeCheckinId) {
      setActiveTab('checkins');
    } else if (activeInquiryId || activeChatId?.startsWith('support_')) {
      setActiveTab('inquiries');
    }
  }, [tabFromUrl, activeCheckinId, activeInquiryId, activeChatId]);

  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [clientFilter, setClientFilter] = useState('all');
  const [sortFilter, setSortFilter] = useState('newest');

  // Pagination & Data state
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const [allConversations, setAllConversations] = useState<ChatConversationItem[]>([]);
  const [hasMore, setHasMore] = useState(true);

  // Fetch client options for filter
  const { data: clientsData } = useGetTrainerClients('all', '', 'name_asc', 1, 1000);
  const clientOptions = useMemo(() => {
    const list = clientsData?.clients || [];
    return [
      { key: 'all', label: 'All Clients' },
      ...list.map((c) => ({ key: c.uid, label: `${c.name} ${c.surname}` })),
    ];
  }, [clientsData]);

  const clientInfoMap = useMemo(() => {
    const map: Record<string, { fullName: string; profilePicture: string | null }> = {};
    (clientsData?.clients || []).forEach((c) => {
      const fullName = `${c.name || ''} ${c.surname || ''}`.trim() || c.email || 'Client';
      map[c.uid] = {
        fullName,
        profilePicture: c.avatarUrl || null,
      };
    });
    return map;
  }, [clientsData]);

  // Realtime updates
  const { realtimeUpdates } = useChatRealtime();
  const { inquiries, isLoading: isLoadingInquiries } = useInquiriesRealtime(trainerUid);
  const { checkins: realtimeCheckins } = useCheckinsRealtime(trainerUid);

  const prevFiltersRef = useRef({ clientFilter, sortFilter, debouncedSearch });
  const [isFetchingPage, setIsFetchingPage] = useState(false);

  const {
    data: pageData,
    isFetching,
    isLoading: isMessagesLoading,
  } = useGetTrainerMessagesPage(
    {
      search: debouncedSearch,
      clientId: clientFilter,
      sort: sortFilter,
      limit: 30,
      cursor,
    },
    !isFetchingPage,
  );

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 500);
    return () => clearTimeout(handler);
  }, [searchQuery]);

  useEffect(() => {
    const prev = prevFiltersRef.current;
    if (
      prev.clientFilter !== clientFilter ||
      prev.sortFilter !== sortFilter ||
      prev.debouncedSearch !== debouncedSearch
    ) {
      prevFiltersRef.current = { clientFilter, sortFilter, debouncedSearch };
      setAllConversations([]);
      setCursor(undefined);
      setHasMore(true);
      setIsFetchingPage(false);
    }
  }, [clientFilter, sortFilter, debouncedSearch]);

  useEffect(() => {
    if (pageData && !isFetching) {
      setAllConversations((prev) => {
        const existingIds = new Set(prev.map((c) => c.chatId));
        const newItems = pageData.items.filter((c) => !existingIds.has(c.chatId));
        return [...prev, ...newItems];
      });
      setHasMore(pageData.hasMore);
      setIsFetchingPage(true);
    }
  }, [pageData, isFetching]);

  const loadNextPage = () => {
    if (pageData?.nextCursor && hasMore) {
      setCursor(pageData.nextCursor);
      setIsFetchingPage(false);
    }
  };

  const displayConversations = useMemo(() => {
    let merged = [...allConversations];

    Object.values(realtimeUpdates).forEach((updatedChat) => {
      if (clientFilter !== 'all' && updatedChat.client.uid !== clientFilter) {
        return;
      }

      if (debouncedSearch) {
        const term = debouncedSearch.toLowerCase();
        if (!updatedChat.lastMessage.body.toLowerCase().includes(term)) {
          return;
        }
      }

      const idx = merged.findIndex((c) => c.chatId === updatedChat.chatId);
      if (idx !== -1) {
        merged[idx] = { ...merged[idx], ...updatedChat };
      } else {
        merged.push(updatedChat);
      }
    });

    merged.sort((a, b) => {
      if (sortFilter === 'newest') {
        return (
          new Date(b.lastMessage.sendTime).getTime() - new Date(a.lastMessage.sendTime).getTime()
        );
      } else if (sortFilter === 'oldest') {
        return (
          new Date(a.lastMessage.sendTime).getTime() - new Date(b.lastMessage.sendTime).getTime()
        );
      } else if (sortFilter === 'name_asc') {
        return (a.client.fullName || '').localeCompare(b.client.fullName || '');
      }
      return 0;
    });

    const seen = new Set();
    merged = merged.filter((c) => {
      if (seen.has(c.chatId)) return false;
      seen.add(c.chatId);
      return true;
    });

    if (Object.keys(clientInfoMap).length > 0) {
      merged = merged.filter((c) => !!clientInfoMap[c.client.uid]);
    }

    merged = merged.map((c) => {
      const info = clientInfoMap[c.client.uid];
      if (info) {
        return {
          ...c,
          client: {
            ...c.client,
            fullName:
              info.fullName && info.fullName !== 'Client'
                ? info.fullName
                : c.client.fullName !== 'Unknown' && c.client.fullName !== 'Client'
                  ? c.client.fullName
                  : info.fullName || 'Client',
            profilePicture: info.profilePicture || c.client.profilePicture,
          },
        };
      }
      return c;
    });

    // Remove support conversations from normal messages tab
    merged = merged.filter(
      (c) =>
        !c.isSupport &&
        !c.chatId?.startsWith('support_') &&
        c.client?.fullName !== 'miniPT Support',
    );

    return merged;
  }, [allConversations, realtimeUpdates, clientFilter, sortFilter, debouncedSearch, clientInfoMap]);

  useEffect(() => {
    if (onConversationsLoaded) {
      onConversationsLoaded(displayConversations);
    }
  }, [displayConversations, onConversationsLoaded]);

  // Unread badge indicators
  const unreadMessagesCount = useMemo(() => {
    return displayConversations.filter((c) => {
      const isSentByTrainer =
        c.lastMessage?.senderUid === trainerUid ||
        (c.lastMessage as any)?.sender_uid === trainerUid;
      return c.isUnread && !isSentByTrainer;
    }).length;
  }, [displayConversations, trainerUid]);

  const unreadInquiriesCount = useMemo(() => {
    return inquiries.filter((inq) => {
      const isSentByTrainer =
        inq.message?.sender_role === 'trainer' ||
        inq.message?.sender_uid === trainerUid ||
        inq.message?.send_uid === trainerUid;
      return !isSentByTrainer && !inq.message.is_read;
    }).length;
  }, [inquiries, trainerUid]);

  const unreadCheckinsCount = useMemo(() => {
    return realtimeCheckins.filter((c) => !c.is_read && (c.isAppoint || Boolean(c.appointUId)))
      .length;
  }, [realtimeCheckins]);

  const formatMessageTime = (isoString?: string) => {
    if (!isoString) return '';
    const date = new Date(isoString);
    if (isNaN(date.getTime())) return '';
    const now = new Date();
    const isToday =
      date.getDate() === now.getDate() &&
      date.getMonth() === now.getMonth() &&
      date.getFullYear() === now.getFullYear();

    const yesterday = new Date(now);
    yesterday.setDate(now.getDate() - 1);
    const isYesterday =
      date.getDate() === yesterday.getDate() &&
      date.getMonth() === yesterday.getMonth() &&
      date.getFullYear() === yesterday.getFullYear();

    const timeStr = date.toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });

    if (isToday) return timeStr;
    if (isYesterday) return 'Yesterday';

    return date.toLocaleDateString([], {
      month: 'short',
      day: 'numeric',
      year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
    });
  };

  const getInitials = (fullName: string) => {
    if (!fullName) return 'U';
    const parts = fullName.trim().split(' ');
    if (parts.length > 1) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return fullName.substring(0, 2).toUpperCase();
  };

  const handleTabChange = (tab: 'messages' | 'inquiries' | 'checkins') => {
    setActiveTab(tab);
    navigate(`/onboarding-trainer/dashboard/messages?tab=${tab}`);
  };

  const getSearchPlaceholder = () => {
    if (activeTab === 'inquiries') return 'Search inquiries';
    if (activeTab === 'checkins') return 'Search check-ins';
    return 'Search messages';
  };

  const filteredInquiries = useMemo(() => {
    return inquiries.filter((inq) => {
      const isSupport =
        Boolean(inq.message.is_support) ||
        inq.chatId.startsWith('support_') ||
        inq.message.sender_name === 'miniPT Support';
      const clientUid = inq.chatId.split('_').find((id) => id !== currentUser?.uid);
      const clientFromConvo = displayConversations.find((c) => c.chatId === inq.chatId)?.client;
      const clientFromMap = clientUid ? clientInfoMap[clientUid] : undefined;
      const clientFullName = isSupport
        ? 'miniPT Support'
        : clientFromConvo?.fullName || clientFromMap?.fullName || inq.message.sender_name || '';

      if (clientFilter && clientFilter !== 'all') {
        if (isSupport) return false;
        if (clientUid !== clientFilter) return false;
      }
      if (debouncedSearch) {
        const q = debouncedSearch.toLowerCase();
        const nameMatch = clientFullName.toLowerCase().includes(q);
        const bodyMatch =
          (inq.message.body || '').toLowerCase().includes(q) ||
          (inq.message.inquiry_text || '').toLowerCase().includes(q);
        const subjectMatch = (inq.message.inquirySubject || inq.message.subject || '')
          .toLowerCase()
          .includes(q);
        const detailMatch = (inq.message.inquiryDetail || '').toLowerCase().includes(q);
        const typeMatch = (
          inq.message.inquiryType ||
          inq.message.topic ||
          inq.message.category ||
          inq.message.inquiry_category ||
          ''
        )
          .toLowerCase()
          .includes(q);
        if (!nameMatch && !bodyMatch && !subjectMatch && !detailMatch && !typeMatch) return false;
      }
      return true;
    });
  }, [
    inquiries,
    displayConversations,
    clientInfoMap,
    clientFilter,
    debouncedSearch,
    currentUser?.uid,
  ]);

  // Combined filter menu for the compact filter button
  const filterMenuItems = useMemo(() => {
    return [
      {
        key: 'sort-group',
        type: 'group' as const,
        label: 'SORT BY',
        children: SORT_ITEMS.map((item) => ({
          key: `sort:${item.key}`,
          label: `${item.label} ${sortFilter === item.key ? '✓' : ''}`,
        })),
      },
      {
        type: 'divider' as const,
      },
      {
        key: 'client-group',
        type: 'group' as const,
        label: 'FILTER CLIENT',
        children: clientOptions.slice(0, 15).map((opt) => ({
          key: `client:${opt.key}`,
          label: `${opt.label} ${clientFilter === opt.key ? '✓' : ''}`,
        })),
      },
      {
        type: 'divider' as const,
      },
      {
        key: 'notifications:manage',
        label: isPushLoading
          ? 'Push Notifications: Checking...'
          : isPushEnabled
            ? 'Push Notifications: Active'
            : 'Push Notifications: Disabled',
      },
    ];
  }, [sortFilter, clientOptions, clientFilter, isPushEnabled, isPushLoading]);

  const handleFilterMenuClick = ({ key }: { key: string }) => {
    if (key === 'notifications:manage') {
      setPushManagerOpen(true);
    } else if (key.startsWith('sort:')) {
      setSortFilter(key.replace('sort:', ''));
    } else if (key.startsWith('client:')) {
      setClientFilter(key.replace('client:', ''));
    }
  };

  return (
    <div className="messages-side-panel">
      {/* Side Panel Header */}
      <div className="side-panel-header">
        <h1 className="side-panel-title">Messages</h1>
      </div>

      {/* Compact Search & Filter Row */}
      <div className="side-panel-search-row">
        <div className="search-input-box">
          <SearchIcon className="search-icon" />
          <input
            type="text"
            placeholder={getSearchPlaceholder()}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onBlur={(e) => setSearchQuery(e.target.value.trim())}
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="clear-search-btn"
              aria-label="Clear search"
            >
              <ClearSearchIcon />
            </button>
          )}
        </div>

        <Dropdown
          menu={{ items: filterMenuItems, onClick: handleFilterMenuClick }}
          trigger={['click']}
          placement="bottomRight"
          rootClassName="dark-theme-dropdown"
        >
          <button
            className={`filter-icon-btn ${clientFilter !== 'all' || sortFilter !== 'newest' ? 'active' : ''}`}
            aria-label="Filter conversations"
            title="Filter & Sort"
          >
            <FilterIcon />
          </button>
        </Dropdown>
      </div>

      {/* Pill Segmented Tabs */}
      <div className="side-panel-tabs">
        <button
          className={`side-tab-pill ${activeTab === 'messages' ? 'active' : ''}`}
          onClick={() => handleTabChange('messages')}
        >
          <span className="tab-label-wrap">
            Messages
            {unreadMessagesCount > 0 && <span className="unread-dot" />}
          </span>
        </button>
        <button
          className={`side-tab-pill ${activeTab === 'inquiries' ? 'active' : ''}`}
          onClick={() => handleTabChange('inquiries')}
        >
          <span className="tab-label-wrap">
            Inquiries
            {unreadInquiriesCount > 0 && <span className="unread-dot" />}
          </span>
        </button>
        <button
          className={`side-tab-pill ${activeTab === 'checkins' ? 'active' : ''}`}
          onClick={() => handleTabChange('checkins')}
        >
          <span className="tab-label-wrap">
            Check-ins
            {unreadCheckinsCount > 0 && <span className="unread-dot" />}
          </span>
        </button>
      </div>

      {/* Side Panel Content List */}
      <div className="side-panel-content-area" id="sidePanelScrollable">
        {activeTab === 'messages' && (
          <>
            {(isFetching || isMessagesLoading || pageData === undefined) &&
            allConversations.length === 0 ? (
              <div className="side-panel-loading">
                <Spin indicator={<LoadingOutlined spin style={{ color: '#F59E0B' }} />} />
                <span>Loading conversations...</span>
              </div>
            ) : displayConversations.length > 0 ? (
              <div className="side-panel-list">
                <InfiniteScroll
                  dataLength={displayConversations.length}
                  next={loadNextPage}
                  hasMore={hasMore}
                  scrollableTarget="sidePanelScrollable"
                  loader={
                    <div className="side-panel-loader">
                      <Spin indicator={<LoadingOutlined spin style={{ color: '#F59E0B' }} />} />
                    </div>
                  }
                >
                  {displayConversations.map((convo) => {
                    const isSelected = convo.chatId === activeChatId;
                    const isSupportConvo =
                      convo.isSupport === true || convo.lastMessage.messageType === 'support';
                    const displayName = convo.client.fullName;

                    return (
                      <div
                        key={convo.chatId}
                        className={`side-item-card conversation-item ${isSelected ? 'active' : ''}`}
                        onClick={() =>
                          navigate(`/onboarding-trainer/dashboard/messages/${convo.chatId}`)
                        }
                      >
                        <div
                          className={`item-avatar ${convo.client.profilePicture ? 'has-image' : ''} ${isSupportConvo ? 'is-support-avatar' : ''}`}
                          style={
                            !isSupportConvo && convo.client.profilePicture
                              ? { backgroundImage: `url(${convo.client.profilePicture})` }
                              : undefined
                          }
                        >
                          {isSupportConvo ? (
                            <SupportHeadsetIcon className="support-headset-icon" />
                          ) : (
                            !convo.client.profilePicture && getInitials(displayName)
                          )}
                        </div>
                        <div className="item-details">
                          <div className="item-row-top">
                            <h4 className="item-name">{displayName}</h4>
                            <div className="item-time-wrap">
                              {convo.isUnread &&
                                convo.lastMessage?.senderUid !== trainerUid &&
                                (convo.lastMessage as any)?.sender_uid !== trainerUid && (
                                  <span className="unread-dot" />
                                )}
                              <span className="item-time">
                                {formatMessageTime(convo.lastMessage.sendTime)}
                              </span>
                            </div>
                          </div>
                          <div className="item-row-preview">
                            <p
                              className={`preview-text ${
                                convo.isUnread &&
                                convo.lastMessage?.senderUid !== trainerUid &&
                                (convo.lastMessage as any)?.sender_uid !== trainerUid
                                  ? 'unread'
                                  : ''
                              }`}
                            >
                              {convo.lastMessage.messageType !== 'text' &&
                              convo.lastMessage.messageType !== 'support'
                                ? `[${convo.lastMessage.messageType.toUpperCase()}]`
                                : convo.lastMessage.body || 'No messages yet'}
                            </p>
                            {convo.isUnread &&
                              convo.lastMessage?.senderUid !== trainerUid &&
                              (convo.lastMessage as any)?.sender_uid !== trainerUid && (
                                <span className="unread-badge">
                                  <span className="status-dot" />
                                  New
                                </span>
                              )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </InfiniteScroll>
              </div>
            ) : (
              <div className="side-panel-empty">
                <EmptyMessagesIcon />
                <p>
                  {debouncedSearch
                    ? `No conversations for "${debouncedSearch}"`
                    : 'No conversations yet'}
                </p>
              </div>
            )}
          </>
        )}

        {activeTab === 'inquiries' && (
          <div className="side-panel-inquiries-wrap">
            <InquiriesList
              inquiries={filteredInquiries}
              isLoading={isLoadingInquiries}
              conversations={displayConversations}
              activeChatId={activeChatId}
              activeInquiryId={activeInquiryId}
            />
          </div>
        )}

        {activeTab === 'checkins' && (
          <div className="side-panel-checkins-wrap">
            <CheckinsList
              onSelectCheckin={(checkinId) =>
                navigate(`/onboarding-trainer/dashboard/messages/checkin/${checkinId}`)
              }
              searchQuery={searchQuery}
              clientFilter={clientFilter}
              sortFilter={sortFilter}
              activeCheckinId={activeCheckinId}
            />
          </div>
        )}
      </div>

      {/* Push Notification Settings Modal */}
      <PushDevicesManager open={pushManagerOpen} onClose={() => setPushManagerOpen(false)} />
    </div>
  );
};
